import { OutlinedTextFieldProps, TextField } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, getIn } from "formik";
import { NumericFormat } from "react-number-format";
import { inputStyles } from "./styles";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      width: ({ width }: { width?: string }) => width || "100%",
      "& > div": {
        backgroundColor: theme.white00,
      },
      ...inputStyles,
    },
  }),
);

type NumberFormatInputProps = Omit<OutlinedTextFieldProps, "variant"> & {
  "data-test-id"?: string;
  width?: string;
  allowDecimals?: boolean;
  allowNegative?: boolean;
  decimalScale?: number;
  validateOnBlur?: boolean;
};

export function NumberFormatInput({
  "data-test-id": dataTestId,
  allowDecimals = false,
  allowNegative = true,
  decimalScale = 6,
  validateOnBlur,
  ...props
}: NumberFormatInputProps) {
  const classes = useStyles({ width: props.width });

  const maxValueLength = 16;
  const userLocale =
    navigator.languages?.length > 0
      ? navigator.languages[0]
      : navigator.language;

  const decimalSeparator = new Intl.NumberFormat(userLocale)
    .formatToParts(1111.1)
    .find((part) => part.type === "decimal")?.value;

  const thousandSeparator = new Intl.NumberFormat(userLocale)
    .formatToParts(11111)
    .find((part) => part.type === "group")?.value;

  return (
    <Field name={props.name}>
      {({ field, form, meta }: { field: any; form: any; meta: any }) => {
        const error =
          meta.error || props.error || getIn(form.status, field.name);

        return (
          <NumericFormat
            allowNegative={allowNegative}
            name={props.name}
            value={field.value || ""}
            label={props.label}
            customInput={TextField}
            thousandsGroupStyle="thousand"
            decimalScale={allowDecimals ? decimalScale : 0}
            decimalSeparator={decimalSeparator}
            thousandSeparator={thousandSeparator}
            onValueChange={(values) =>
              form.setFieldValue(props.name, values.floatValue)
            }
            onBlur={(e) => {
              field.onBlur(e);
              if (form.status?.[field.name]) {
                const { [field.name]: _fieldError, ...rest } = form.status;
                form.setStatus(rest);
              }
              if (validateOnBlur) {
                form.validateField(field.name);
              }
            }}
            helperText={error || props.helperText}
            error={!!error}
            inputProps={{
              ...props.inputProps,
              "data-test-id": dataTestId || "",
            }}
            InputProps={props.InputProps}
            className={`${classes.input} ${props.className ? props.className : ""} `}
            isAllowed={(values) => {
              if (typeof values.value !== "undefined") {
                const { value } = values;
                return value.length <= maxValueLength;
              } else {
                return false;
              }
            }}
          />
        );
      }}
    </Field>
  );
}
